import { Camera, CameraResultType, CameraSource, MediaTypeSelection, type MediaResult, type PermissionStatus } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import type { AlbumSaveResult, CapturedFile, LibraryPickOptions, MediaAdapter } from './mediaContract';
import { webMediaAdapter } from './webMediaAdapter';

// Native media lanes of the app (iPhone and Android). Design points, each a knowledge import (manifest K-15):
//   - the plugin is imported STATICALLY — a dynamic import() chunk that never resolves on
//     device is a documented old-line failure mode (picker hang) that we do not reproduce;
//   - `saveToGallery: true` makes the plugin write the captured image into the Photos library
//     as part of the capture step itself — i.e. before JS receives the photo, before decode,
//     before persistence, before any checklist/finding work (OWNER-CONTRACT §6.1);
//   - the LIBRARY lane is the system's own photo picker (Owner 2026-10-28: 「原生相册既然更好，就换原生相册
//     做法」, replacing the 2026-08-25 ruling that kept it a file input). On iOS that is Apple's PHPicker, in the
//     phone's language, through the plugin's pickImages: its newer chooseFromGallery opened IONCameraLib's own
//     grid instead, titled 「Photo Library · Cancel · Done」 in English on a Chinese iPhone (TestFlight 215 on the
//     test phone, 2026-10-28; Owner: 「换成苹果标准选择器」). Android keeps chooseFromGallery. The phone scales each
//     photo to LIBRARY_MAX_EDGE before handing it over (the report prints 1200 px, the AI reads 1280 px), so
//     150–200 photos come in without the web view decoding every 12-megapixel original, and the photos are
//     imported in the order they were TAKEN;
//   - cancellation is a neutral outcome (plugin rejects with a "cancel" message → null / []);
//   - the album outcome is derived from the Photos permission the OS actually granted, so a
//     refused permission surfaces as 'failed' on the photo instead of a silent miss.
//
// Windows has no Xcode: this file is type-checked and bundled here, and its behaviour on a
// device is DEVICE TEST REQUIRED (reported as such, never claimed).
type PhotosPermission = PermissionStatus['photos'];

let lastPhotosPermission: PhotosPermission | 'unknown' = 'unknown';

// The longest edge of a photo picked from the library, scaled on the phone.
export const LIBRARY_MAX_EDGE = 2400;

// A photo the picker handed over, whichever lane picked it.
export interface PickedPhoto {
  path: string | undefined;
  format: string;
  takenAt: number | null;
}

// The EXIF capture time ("2026:10:26 14:03:22", read as UTC — only the order matters), from the {Exif}
// dictionary pickImages returns or the JSON text chooseFromGallery returns.
export function exifTakenAt(exif: unknown): number | null {
  const text = typeof exif === 'string' ? exif : exif && typeof exif === 'object' ? JSON.stringify(exif) : '';
  const m = /DateTimeOriginal\W+(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(text);
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]) : null;
}

// When a photo chooseFromGallery picked was taken: the creation date it reports (ISO 8601), else the EXIF time.
export function takenAtOf(result: Pick<MediaResult, 'metadata'>): number | null {
  const created = result.metadata?.creationDate ? Date.parse(result.metadata.creationDate) : Number.NaN;
  if (Number.isFinite(created)) return created;
  return exifTakenAt(result.metadata?.exif);
}

// The picked photos in the order they were taken; a photo without a time keeps its place after the dated ones.
export function orderedByTakenAt<T extends Pick<PickedPhoto, 'takenAt'>>(photos: readonly T[]): T[] {
  const dated = photos.map((photo, index) => ({ photo, index }));
  return [...dated.filter((p) => p.photo.takenAt !== null).sort((a, b) => (a.photo.takenAt as number) - (b.photo.takenAt as number) || a.index - b.index), ...dated.filter((p) => p.photo.takenAt === null)].map((p) => p.photo);
}

// iOS: Apple's PHPicker. Android: the plugin's chooseFromGallery. Both scale on the phone and report the time.
async function pickPhotos(single: boolean): Promise<PickedPhoto[]> {
  if (Capacitor.getPlatform() === 'ios') {
    const picked = await Camera.pickImages({ limit: single ? 1 : 0, quality: 90, width: LIBRARY_MAX_EDGE, height: LIBRARY_MAX_EDGE, correctOrientation: true });
    return picked.photos.map((photo) => ({ path: photo.webPath, format: photo.format || 'jpeg', takenAt: exifTakenAt(photo.exif) }));
  }
  const picked = await Camera.chooseFromGallery({
    mediaType: MediaTypeSelection.Photo,
    allowMultipleSelection: !single,
    limit: single ? 1 : 0,
    includeMetadata: true,
    quality: 90,
    targetWidth: LIBRARY_MAX_EDGE,
    targetHeight: LIBRARY_MAX_EDGE,
    correctOrientation: true
  });
  return picked.results.map((result) => ({ path: result.webPath ?? result.uri, format: result.metadata?.format || 'jpeg', takenAt: takenAtOf(result) }));
}

async function capturedFromGallery(photo: PickedPhoto, index: number): Promise<CapturedFile | null> {
  if (!photo.path) return null;
  const response = await fetch(photo.path);
  const blob = await response.blob();
  const format = photo.format.replace(/^jpg$/i, 'jpeg');
  return { blob, mimeType: blob.type && blob.type.length > 0 ? blob.type : `image/${format}`, fileName: `IMG-${Date.now()}-${index + 1}.${format === 'jpeg' ? 'jpg' : format}`, source: 'photoLibrary' };
}

function albumWritable(state: PhotosPermission | 'unknown'): boolean {
  return state === 'granted' || state === 'limited';
}

async function ensurePermissions(): Promise<PermissionStatus> {
  let status = await Camera.checkPermissions();
  const wanted: Array<'camera' | 'photos'> = [];
  if (status.camera !== 'granted') wanted.push('camera');
  if (!albumWritable(status.photos)) wanted.push('photos');
  if (wanted.length > 0) status = await Camera.requestPermissions({ permissions: wanted });
  return status;
}

export const iosMediaAdapter: MediaAdapter = {
  platform: 'ios',
  async captureFromCamera(): Promise<CapturedFile | null> {
    const permissions = await ensurePermissions();
    lastPhotosPermission = permissions.photos;
    if (permissions.camera !== 'granted') {
      throw new Error('相机权限未授予：请到 iOS 设置 → 本 App → 相机 开启。');
    }
    let webPath: string | undefined;
    let format = 'jpeg';
    try {
      const photo = await Camera.getPhoto({
        source: CameraSource.Camera,
        resultType: CameraResultType.Uri,
        saveToGallery: true,
        correctOrientation: true,
        quality: 92
      });
      webPath = photo.webPath;
      format = photo.format || 'jpeg';
    } catch (raw) {
      const text = raw instanceof Error ? raw.message : String(raw);
      if (/cancel/i.test(text)) return null;
      throw new Error(`相机调用失败：${text}`);
    }
    if (!webPath) throw new Error('相机未返回照片文件。');
    const response = await fetch(webPath);
    const blob = await response.blob();
    const mimeType = blob.type && blob.type.length > 0 ? blob.type : `image/${format}`;
    return { blob, mimeType, fileName: `IMG-${Date.now()}.${format}`, source: 'camera' };
  },
  async pickFromLibrary(options: LibraryPickOptions = {}): Promise<CapturedFile[]> {
    const single = options.single === true;
    let photos: PickedPhoto[];
    try {
      photos = await pickPhotos(single);
    } catch (raw) {
      const text = raw instanceof Error ? raw.message : String(raw);
      if (/cancel/i.test(text)) return [];
      // A phone whose picker cannot be opened (or whose photo access was refused) still imports, through the
      // file input — in the app that is the system picker too, without access to the whole library.
      return webMediaAdapter.pickFromLibrary(options);
    }
    const ordered = orderedByTakenAt(single ? photos.slice(0, 1) : photos);
    const files: CapturedFile[] = [];
    for (let index = 0; index < ordered.length; index += 1) {
      const file = await capturedFromGallery(ordered[index], index);
      if (file) files.push(file);
    }
    return files;
  },
  async saveToSystemAlbum(): Promise<AlbumSaveResult> {
    // The write itself happened inside Camera.getPhoto (saveToGallery). Report what the OS
    // permission allows so a refusal is visible on the photo, never silent.
    if (albumWritable(lastPhotosPermission)) {
      return {
        state: 'saved',
        detail:
          lastPhotosPermission === 'limited'
            ? '由相机插件在拍摄时写入系统相册（照片权限为「部分」）。'
            : '由相机插件在拍摄时写入系统相册。'
      };
    }
    return {
      state: 'failed',
      detail: '相册写入权限未授予：照片目前只在 App 内。请到 iOS 设置 → 本 App → 照片 允许「添加照片」。'
    };
  }
};
